import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyResponseType } from '@services';
import { Anchor, Text } from '@/shared/ui';
import {
  collectMediaLinkRows,
  collectRentalConditionRows,
  collectSaleConditionRows,
  collectTagValues,
  type DetailFieldRow,
} from '@/shared/utils/property-detail-fields';
import {
  collectAuctionRows,
  collectConditionRows,
  collectShortTermRows,
  type AttributeRow,
} from '@/shared/utils/property-attribute-rows';
import { SectionCard, InfoRow } from '../SectionCard';

interface OverviewExtraSectionsProps {
  property: PropertyResponseType;
}

/**
 * Localizes a detail-field value when it is an option code or yes-flag.
 */
function formatRowValue(row: DetailFieldRow, t: (key: string, o?: { defaultValue?: string }) => string): string {
  if (row.value === 'yes') return t('detailView.terms.yes');
  const optionGroups = ['rentalPaymentFrequency', 'administrationType', 'rentalIncludedExpenses'];
  for (const group of optionGroups) {
    const key = `form.options.${group}.${row.value}`;
    const label = t(key, { defaultValue: '' });
    if (label && label !== key) return label;
  }
  return row.value;
}

/**
 * Renders saved tags, commercial terms and media links on the overview tab.
 */
/**
 * The Temporada and Leilão cards, side by side on wide screens. Each card
 * shows only when its block holds something, like every card on this tab.
 *
 * @param props - The rows of each card.
 * @returns The pair of cards, or null when both are empty.
 */
function PricingCards({ shortTerm, auction }: { shortTerm: AttributeRow[]; auction: AttributeRow[] }) {
  const { t } = useTranslation('features/properties');
  if (shortTerm.length === 0 && auction.length === 0) return null;
  return (
    <div className="grid grid-cols-1 gap-[var(--space-4)] md:grid-cols-2">
      {shortTerm.length > 0 && (
        <SectionCard title={t('detailView.shortTerm.title')} data-testid="property-short-term-card">
          {shortTerm.map((row) => <InfoRow key={row.labelKey} label={t(row.labelKey)} value={row.value} />)}
        </SectionCard>
      )}
      {auction.length > 0 && (
        <SectionCard title={t('detailView.auction.title')} data-testid="property-auction-card">
          {auction.map((row) => <InfoRow key={row.labelKey} label={t(row.labelKey)} value={row.value} />)}
        </SectionCard>
      )}
    </div>
  );
}

export const OverviewExtraSections: React.FC<OverviewExtraSectionsProps> = ({ property }) => {
  const { t } = useTranslation('features/properties');
  const tags = collectTagValues(property.metadata);
  const terms = [
    ...collectSaleConditionRows(property.pricing?.sale),
    ...collectRentalConditionRows(property.pricing?.rental),
  ];
  const conditions = collectConditionRows(property.characteristics, t);
  const shortTerm = collectShortTermRows(property.pricing?.short_term, t);
  const auction = collectAuctionRows(property.pricing?.auction);
  const media = collectMediaLinkRows(property.media);

  return (
    <>
      {terms.length + conditions.length > 0 && (
        <SectionCard title={t('detailView.terms.title')} data-testid="property-conditions-card">
          {terms.map((row) => (
            <InfoRow key={row.labelKey} label={t(row.labelKey)} value={formatRowValue(row, t)} />
          ))}
          {conditions.map((row) => (
            <InfoRow key={row.labelKey} label={t(row.labelKey)} value={row.value} />
          ))}
        </SectionCard>
      )}

      <PricingCards shortTerm={shortTerm} auction={auction} />

      {tags.length > 0 && (
        <SectionCard title={t('detailView.overview.tags')}>
          <div className="flex flex-wrap gap-[var(--space-2)]">
            {tags.map((tag) => (
              <Text
                as="span"
                key={tag}
                className="px-[var(--space-3)] py-[var(--space-1)] rounded-(--radius-md) bg-[color-mix(in_srgb,var(--seed-base)_6%,var(--surface-primary))] text-[var(--text-primary)] text-[length:var(--text-meta)] border border-[color-mix(in_srgb,var(--seed-base)_15%,transparent)]"
              >
                {tag}
              </Text>
            ))}
          </div>
        </SectionCard>
      )}

      {media.length > 0 && (
        <SectionCard title={t('detailView.media.title')}>
          {media.map((row) => (
            <div key={row.labelKey} className="flex justify-between items-center py-[var(--space-2)] gap-[var(--space-3)]">
              <Text as="span" className="text-[length:var(--text-body-sm)] text-[var(--text-tertiary)] shrink-0">
                {t(row.labelKey)}
              </Text>
              <Anchor
                href={row.value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[length:var(--text-body-sm)] font-bold text-[var(--seed-base)] truncate underline-offset-2 hover:underline"
              >
                {row.value}
              </Anchor>
            </div>
          ))}
        </SectionCard>
      )}
    </>
  );
};
